/**
 * The shared DS 2.0 focus ring.
 *
 * Tailwind must see every complete class name at build time, so the selector variants live in this
 * static map rather than being assembled from string fragments. All variants draw the same effect:
 * the opaque inner gap at the library's 2px spread, over the translucent outer halo at 4px.
 */
const focusRingClasses = {
  /** A control that owns its `:focus-visible` state. */
  self: [
    'focus-visible:outline-none',
    'focus-visible:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--effect-focus-inner),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--effect-focus-outer)]',
  ].join(' '),

  /** A field box whose nested input owns `:focus-visible`; preserves its two inset stroke layers. */
  inputWithin: [
    'has-[input:focus-visible]:outline-none',
    'has-[input:focus-visible]:shadow-[inset_0_0_0_0.5px_var(--ds-datepicker-stroke-extra),inset_0_0_0_2.5px_var(--ds-datepicker-ring-current),var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--effect-focus-inner),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--effect-focus-outer)]',
  ].join(' '),

  /** Checkbox focus lives on its parent control while the visual ring belongs to the inner box. */
  checkbox: [
    'group-focus-visible/cb:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--effect-focus-inner),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--effect-focus-outer)]',
  ].join(' '),

  /** Menu rows sit on a secondary panel, so their inner gap must use that panel's own fill. */
  menuItem: [
    'focus-visible:outline-none',
    'focus-visible:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--ds-menu-focus-gap),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--ds-menu-focus)]',
  ].join(' '),

  /** Select rows use the same panel-aware gap through their own slot family. */
  selectItem: [
    'focus-visible:outline-none',
    'focus-visible:shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--ds-select-item-focus-gap),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--ds-select-item-focus)]',
  ].join(' '),
} as const;

export type FocusRingTarget = keyof typeof focusRingClasses;

export const focusRing = (target: FocusRingTarget = 'self') => focusRingClasses[target];
